/**
 * Slider.jsx
 * Created by Michael Stanaland on 06/22/18.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { throttle } from 'lodash';

import * as Icons from './icons/Icons';

import { animatedScrollTo } from '../../helpers/scrollToHelper';

const propTypes = {
    children: PropTypes.node.isRequired
};

export default class Slider extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            scrolledLeft: true,
            scrolledRight: true
        };
        
        this.scrollWidth = 0;

        this.setNavButtons = this.setNavButtons.bind(this);
        this.handleLeftNavClick = this.handleLeftNavClick.bind(this);
        this.handleRightNavClick = this.handleRightNavClick.bind(this);
        this.throttledSetNavButtons = throttle(this.setNavButtons, 300);
    }

    componentDidMount() {
        // Add event listener for resize and scrolling,
        // then manually call the handler to check if we need to add scroll buttons
        window.addEventListener("resize", this.throttledSetNavButtons);
        this.viewport.addEventListener("scroll", this.throttledSetNavButtons);

        this.setNavButtons();
    }
    
    componentDidUpdate() {
        this.setNavButtons();
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.throttledSetNavButtons);
        this.viewport.removeEventListener("scroll", this.throttledSetNavButtons);
    }

    setNavButtons = () => {
        if (typeof this.viewport !== 'object') {
            return;
        }
        
        const scrollWidth = this.viewport.scrollWidth;
        
        // The scrollWidth can change slightly until the page has absolutely fully
        // rendered. We force the component to rerender until the scrollWidth
        // stabalizes.
        if (scrollWidth !== this.scrollWidth) {
            setTimeout(() => {
                this.scrollWidth = scrollWidth;
                this.forceUpdate();
            }, 400);
        }
        
        // Checks to see if scroll nav buttons should be disabled or not based on
        // the scroll position, viewport width and content widths
        const scrollPosition = this.viewport.scrollLeft;
        
        const viewportWidth = Math.ceil(this.viewport.getBoundingClientRect().width);
        const scrollEnd = scrollWidth - viewportWidth;

        const scrolledLeft =
            scrollWidth <= viewportWidth || scrollPosition <= 0;
        const scrolledRight =
            scrollWidth <= viewportWidth || scrollPosition >= scrollEnd;

        if (
            scrolledLeft !== this.state.scrolledLeft
            || scrolledRight !== this.state.scrolledRight
        ) {
            this.setState({
                scrolledLeft,
                scrolledRight
            });
        }
    }

    handleLeftNavClick(event) {
        event.preventDefault();
        const currentPosition = this.viewport.scrollLeft;
        const offsetWidth = this.viewport.offsetWidth;
        const newPosition = currentPosition - offsetWidth;
        // change speed based on the distance to travel so all animations appear
        // to be the same speed
        const scrollSpeed = Math.abs(currentPosition - newPosition) * 0.8;

        animatedScrollTo({
            element: this.viewport,
            to: newPosition,
            duration: scrollSpeed,
            scrollDirection: "scrollLeft"
        });
    }

    handleRightNavClick(event) {
        event.preventDefault();
        const currentPosition = this.viewport.scrollLeft;
        const offsetWidth = this.viewport.offsetWidth;
        const newPosition = currentPosition + offsetWidth;
        // change speed based on the distance to travel so all animations appear
        // to be the same speed
        const scrollSpeed = Math.abs(currentPosition - newPosition) * 0.8;

        animatedScrollTo({
            element: this.viewport,
            to: newPosition,
            duration: scrollSpeed,
            scrollDirection: "scrollLeft"
        });
    }

    render() {
        const { children } = this.props;
        const { scrolledLeft, scrolledRight } = this.state;

        const leftNavClasses = cx("rec-slider-left-nav", {
            "disabled": scrolledLeft
        });
        const rightNavClasses = cx("rec-slider-right-nav", {
            "disabled": scrolledRight
        });

        return (
            <div className="rec-slider-container">
                <button
                    className={leftNavClasses}
                    type="button"
                    title="Previous"
                    aria-hidden={scrolledLeft}
                    aria-label="Scroll left for previous options"
                    onClick={this.handleLeftNavClick}>
                    <span className="rec-slider-icon-wrapper">
                        <Icons.IconChevronLeft />
                    </span>
                </button>
                <div
                    className="rec-slider-viewport"
                    ref={(viewport) => {
                        this.viewport = viewport;
                    }}>
                    {children}
                </div>
                <button
                    className={rightNavClasses}
                    type="button"
                    title="More options"
                    aria-hidden={scrolledRight}
                    aria-label="Scroll right for more options"
                    onClick={this.handleRightNavClick}>
                    <span className="rec-slider-icon-wrapper">
                        <Icons.IconChevronRight />
                    </span>
                </button>
            </div>
        );
    }
}

Slider.propTypes = propTypes;